'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PageFrame } from '../ui'
import { ChevronDown, Loader2, RefreshCw, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { useTenants } from './entriesApi'
import type { AccountActivityEventType, AccountActivityLogItem } from './entryTypes'

const PAGE_SIZE = 30

const EVENT_TYPES: { value: AccountActivityEventType; label: string; badgeClass: string }[] = [
  { value: 'account_created', label: 'Account created', badgeClass: 'bg-signal' },
  { value: 'account_linked', label: 'Account linked', badgeClass: 'bg-signal' },
  { value: 'password_setup_requested', label: 'Setup email sent', badgeClass: 'bg-warning' },
  { value: 'password_reset_requested', label: 'Reset email sent', badgeClass: 'bg-warning' },
  { value: 'password_reset_completed', label: 'Password reset', badgeClass: 'bg-success' },
  { value: 'login', label: 'Login', badgeClass: 'bg-violet-500' },
  { value: 'tenant_created', label: 'Tenant created', badgeClass: 'bg-indigo-500' },
]
const EVENT_TYPE_META: Record<string, { label: string; badgeClass: string }> = Object.fromEntries(
  EVENT_TYPES.map((e) => [e.value, e])
)

function EventBadge({ eventType }: { eventType: string }) {
  const meta = EVENT_TYPE_META[eventType]
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white ${meta?.badgeClass ?? 'bg-subtle'}`}>
      {meta?.label ?? eventType}
    </span>
  )
}

function TenantTag({ name }: { name: string }) {
  return <span className="shrink-0 truncate rounded bg-signal-surface px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-signal">{name}</span>
}

// Generic checkbox-in-dropdown multi-select — same interaction shape as
// EmailLogsView's EmailTypeMultiSelect, without the tenant-tag decoration
// that only makes sense for email types.
function MultiSelect({
  theme,
  values,
  onChange,
  options,
  placeholder,
}: {
  theme: ProcessedTheme
  values: string[]
  onChange: (values: string[]) => void
  options: { value: string; label: string }[]
  placeholder: string
}) {
  const c = watchClasses(theme)
  const [open, setOpen] = useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const valueSet = new Set(values)
  const toggle = (v: string) => onChange(valueSet.has(v) ? values.filter((existing) => existing !== v) : [...values, v])
  const remove = (v: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(values.filter((existing) => existing !== v))
  }
  const labelFor = (v: string) => options.find((o) => o.value === v)?.label ?? v

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex min-h-[30px] w-full flex-wrap items-center gap-1 rounded border px-2 py-1 text-left text-[12px] ${c.input}`}
      >
        {values.length === 0 && <span className={c.faint}>{placeholder}</span>}
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded bg-signal-surface px-1.5 py-0.5 text-[11px] text-signal">
            {labelFor(v)}
            <X className="h-2.5 w-2.5 cursor-pointer opacity-70 hover:opacity-100" onClick={(e) => remove(v, e)} />
          </span>
        ))}
        <ChevronDown className={`ml-auto h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${c.faint}`} />
      </button>

      {open && (
        <div className={`absolute z-20 mt-1 max-h-64 min-w-[220px] overflow-y-auto rounded border shadow-notice ${c.panel} ${c.line}`}>
          {options.map((o) => (
            <label key={o.value} className={`flex w-full cursor-pointer items-center gap-1.5 px-3 py-1.5 text-left text-[12px] hover:bg-secondary ${c.text}`}>
              <input type="checkbox" checked={valueSet.has(o.value)} onChange={() => toggle(o.value)} className="shrink-0" />
              <span className="flex-1 truncate">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

type Filters = { eventTypes: string[]; actor: string; tenantId: string; source: string; since: string; until: string; q: string }
const EMPTY_FILTERS: Filters = { eventTypes: [], actor: '', tenantId: '', source: '', since: '', until: '', q: '' }

async function fetchLogs(offset: number, filters: Filters, limit: number = PAGE_SIZE) {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) })
  if (filters.eventTypes.length > 0) params.set('eventType', filters.eventTypes.join(','))
  if (filters.actor) params.set('actor', filters.actor)
  if (filters.tenantId) params.set('tenantId', filters.tenantId)
  if (filters.source) params.set('source', filters.source)
  if (filters.since) params.set('since', filters.since)
  if (filters.until) params.set('until', filters.until)
  if (filters.q) params.set('q', filters.q)

  try {
    const res = await fetch(`/api/entries/account-activity-logs?${params.toString()}`, { credentials: 'include' })
    return await res.json()
  } catch (error) {
    return { success: false, error: String(error) }
  }
}

export function TenantLogsView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const { tenants } = useTenants()

  const [pendingFilters, setPendingFilters] = useState<Filters>(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS)
  const [entries, setEntries] = useState<AccountActivityLogItem[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const load = useCallback(async (offset: number, filters: Filters, replace: boolean, limit: number = PAGE_SIZE) => {
    if (replace) setLoading(true)
    else setLoadingMore(true)
    setError(undefined)

    const result = await fetchLogs(offset, filters, limit)
    if (result.success) {
      const page: AccountActivityLogItem[] = result.data?.entries ?? []
      setEntries((prev) => (replace ? page : [...prev, ...page]))
      setTotal(result.data?.total ?? null)
      setHasMore(Boolean(result.data?.hasMore))
    } else {
      setError(typeof result.error === 'string' ? result.error : 'Failed to load tenant logs')
    }
    setLoading(false)
    setLoadingMore(false)
  }, [])

  useEffect(() => {
    load(0, appliedFilters, true)
  }, [appliedFilters, load])

  const applyFilters = () => setAppliedFilters(pendingFilters)
  const clearFilters = () => {
    setPendingFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
  }

  const tenantName = (tenantId?: string | null) => (tenantId ? tenants.find((t) => t.id === tenantId)?.name : undefined)

  const columns: ColumnDef[] = useMemo(() => [
    { key: 'date', label: 'Date', initialWidth: 130 },
    { key: 'event', label: 'Event', initialWidth: 150 },
    { key: 'actor', label: 'Actor', initialWidth: 140 },
    { key: 'target', label: 'Target', initialWidth: 220 },
    { key: 'tenant', label: 'Tenant', initialWidth: 140 },
    { key: 'source', label: 'Source', initialWidth: 90 },
    { key: 'details', label: 'Details' },
  ], [])

  return (
    <PageFrame padded={false} fill eyebrow="Administration · Logs" title="Tenant logs">
    <div className="flex h-full min-h-0 flex-col overflow-hidden p-4">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Event</span>
          <MultiSelect
            theme={theme}
            values={pendingFilters.eventTypes}
            onChange={(values) => setPendingFilters((p) => ({ ...p, eventTypes: values }))}
            options={EVENT_TYPES.map((e) => ({ value: e.value, label: e.label }))}
            placeholder="All"
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Actor</span>
          <select
            className={`rounded border bg-transparent px-2 py-1.5 text-[12px] ${c.input}`}
            value={pendingFilters.actor}
            onChange={(e) => setPendingFilters((p) => ({ ...p, actor: e.target.value }))}
          >
            <option value="">All</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
            <option value="system">System</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Tenant</span>
          <select
            className={`rounded border bg-transparent px-2 py-1.5 text-[12px] ${c.input}`}
            value={pendingFilters.tenantId}
            onChange={(e) => setPendingFilters((p) => ({ ...p, tenantId: e.target.value }))}
          >
            <option value="">All</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Source</span>
          <select
            className={`rounded border bg-transparent px-2 py-1.5 text-[12px] ${c.input}`}
            value={pendingFilters.source}
            onChange={(e) => setPendingFilters((p) => ({ ...p, source: e.target.value }))}
          >
            <option value="">All</option>
            <option value="backoffice">Backoffice</option>
            <option value="webapp">Webapp</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Since</span>
          <Input
            type="date"
            className={`h-[30px] text-[12px] ${c.input}`}
            value={pendingFilters.since}
            onChange={(e) => setPendingFilters((p) => ({ ...p, since: e.target.value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Until</span>
          <Input
            type="date"
            className={`h-[30px] text-[12px] ${c.input}`}
            value={pendingFilters.until}
            onChange={(e) => setPendingFilters((p) => ({ ...p, until: e.target.value }))}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className={`text-[10px] font-semibold uppercase tracking-widest ${c.label}`}>Search</span>
          <Input
            className={`h-[30px] w-56 text-[12px] ${c.input}`}
            value={pendingFilters.q}
            onChange={(e) => setPendingFilters((p) => ({ ...p, q: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder="Search email or user id…"
          />
        </div>

        <button
          onClick={applyFilters}
          className="h-[30px] rounded bg-primary text-primary-foreground px-3 text-[12px] font-semibold text-white hover:bg-signal"
        >
          Apply
        </button>
        <button
          onClick={clearFilters}
          className={`h-[30px] rounded border px-3 text-[12px] font-medium ${c.line} ${c.faint}`}
        >
          Clear
        </button>

        <span className={`ml-auto self-center text-[12px] ${c.faint}`}>
          {entries.length}{total !== null ? ` / ${total}` : ''} entries
        </span>
      </div>

      {error && <div className="mb-2 text-[12px] text-destructive">{error}</div>}

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-signal" />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <ResizableTable columns={columns} stickyHeader theme={theme} isEmpty={entries.length === 0} emptyMessage="No activity logged yet.">
            {entries.map((entry) => (
              <tr key={entry.id} className={`border-b transition-colors hover:bg-secondary ${c.line}`}>
                <TableTd className={`text-[11px] ${c.faint}`}>{compactDate(entry.created_at)}</TableTd>
                <TableTd><EventBadge eventType={entry.event_type} /></TableTd>
                <TableTd className="truncate text-[11px]">
                  <span className="capitalize">{entry.actor}</span>
                  {entry.actor_ext_user_id && <span className={`ml-1 ${c.faint}`}>({entry.actor_ext_user_id.slice(0, 8)})</span>}
                </TableTd>
                <TableTd className="truncate">{entry.target_email ?? entry.target_ext_user_id}</TableTd>
                <TableTd className="truncate">
                  {tenantName(entry.tenant_id) ? <TenantTag name={tenantName(entry.tenant_id)!} /> : '—'}
                </TableTd>
                <TableTd className="truncate text-[11px] uppercase tracking-wide text-signal">{entry.source}</TableTd>
                <TableTd className="truncate text-[11px] text-destructive">
                  {entry.metadata && typeof entry.metadata.error === 'string' ? entry.metadata.error : ''}
                </TableTd>
              </tr>
            ))}
          </ResizableTable>

          {hasMore && (
            <div className="mt-3 flex justify-center gap-2">
              <button
                onClick={() => load(entries.length, appliedFilters, false)}
                disabled={loadingMore}
                className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${c.line} ${c.faint}`}
              >
                {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Load more
              </button>
              <button
                onClick={() => load(entries.length, appliedFilters, false, 100)}
                disabled={loadingMore}
                className={`flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${c.line} ${c.faint}`}
              >
                {loadingMore ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Load 100 more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </PageFrame>
  )
}
